import {
	CheckOutlined,
	CopyOutlined,
	DislikeFilled,
	DislikeOutlined,
	LikeFilled,
	LikeOutlined,
	LoadingOutlined
} from '@ant-design/icons';
import { copyToClipboard } from '@bimser/core';
import React from 'react';
import BCButton from '../../BCButton';
import BCButtonGroup from '../../BCButtonGroup';
import BCInput, { BCInputRef } from '../../BCInput';
import Styles from '../assets/styles.scss';
import {
	IAssistantMessageFeedbackProps,
	ResponseStatusType
} from '../entities/IAssistantMessageProps';
import { findMessage } from '../../BCIntl';
import { BCNotification } from '../../BCNotification';

const btnStyles = { color: 'rgb(125, 125, 125)' };
const btnGreenStyles = { color: '#008000' };
const btnRedStyles = { color: '#ff0000' };

const AssistantMessageFeedback: React.FC<IAssistantMessageFeedbackProps> = props => {
	const feedbackInput = React.useRef<BCInputRef>();

	const [copyStatus, setCopyStatus] = React.useState<'readyToCopy' | 'copied'>('readyToCopy');
	const [responseStatus, setResponseStatus] = React.useState<ResponseStatusType>();
	const [responseSentStatus, setResponseSentStatus] = React.useState<
		'sent' | 'pending' | 'ready'
	>();
	const [showFeedbackPanel, setShowFeedbackPanel] = React.useState<boolean>();

	React.useEffect(() => {
		let timer;
		if (copyStatus === 'copied' && !timer) {
			timer = setTimeout(() => {
				setCopyStatus('readyToCopy');
				timer = undefined;
			}, 2000);
		}
	}, [copyStatus]);

	React.useEffect(() => {
		if (props.isLastMessage && showFeedbackPanel) {
			props.scrollToBottom();
		}
	}, [props.isLastMessage, showFeedbackPanel]);

	const copyResponse = () => {
		copyToClipboard(props.message.content)
			.then(() => {
				setCopyStatus('copied');
			})
			.catch(console.log);
	};

	const onSubmitResponse = () => {
		setResponseSentStatus('pending');
		props
			.onResponseSent({
				type: responseStatus,
				message: props.message,
				feedback: feedbackInput.current.input.value
			})
			.then(() => {
				setResponseSentStatus('sent');
				setShowFeedbackPanel(false);
			})
			.catch(() => {
				setResponseSentStatus('ready');
				BCNotification.error({
					message: findMessage.get('103566')
				});
			});
	};

	const getCopyButtonIcon = () => {
		return copyStatus === 'copied' ? (
			<CheckOutlined style={btnStyles} />
		) : (
			<CopyOutlined style={btnStyles} />
		);
	};

	const getGoodButtonIcon = () => {
		if (responseSentStatus === 'sent' && responseStatus === 'good') {
			return <LikeFilled style={btnGreenStyles} />;
		} else if (responseSentStatus === 'pending' && responseStatus === 'good') {
			return <LoadingOutlined style={btnStyles} />;
		} else {
			return <LikeOutlined style={responseStatus === 'good' ? btnGreenStyles : btnStyles} />;
		}
	};

	const getBadButtonIcon = () => {
		if (responseSentStatus === 'sent' && responseStatus === 'bad') {
			return <DislikeFilled style={btnRedStyles} />;
		} else if (responseSentStatus === 'pending' && responseStatus === 'bad') {
			return <LoadingOutlined style={btnStyles} />;
		} else {
			return <DislikeOutlined style={responseStatus === 'bad' ? btnRedStyles : btnStyles} />;
		}
	};

	const responseBtnClicked = (type: ResponseStatusType) => {
		if (responseSentStatus === 'sent') return;
		if (responseStatus === type) {
			setResponseStatus(undefined);
			setShowFeedbackPanel(false);
		} else {
			setResponseStatus(type);
			setShowFeedbackPanel(true);
		}
	};

	return (
		<div className={Styles.messageActions}>
			<BCButtonGroup key="AssistantMessageActionButtonGroup">
				<BCButton
					icon={getCopyButtonIcon()}
					type="text"
					size="small"
					title={findMessage.get('100012')}
					onClick={copyResponse}
				/>
				<BCButton
					icon={getGoodButtonIcon()}
					type="text"
					size="small"
					title={findMessage.get('103559')}
					onClick={() => responseBtnClicked('good')}
				/>
				<BCButton
					icon={getBadButtonIcon()}
					type="text"
					size="small"
					title={findMessage.get('103560')}
					onClick={() => responseBtnClicked('bad')}
				/>
			</BCButtonGroup>
			{showFeedbackPanel && (
				<div className={Styles.feedback}>
					<BCInput
						placeHolder={findMessage.get('103561')}
						size="large"
						className={Styles.feedbackInput}
						ref={feedbackInput}
					/>
					<BCButton
						text={findMessage.get('100968')}
						className={Styles.submitBtn}
						type="primary"
						onClick={onSubmitResponse}
					/>
				</div>
			)}
		</div>
	);
};

export { AssistantMessageFeedback };
